import type { AnalysisResult } from '@fantasy/types';

/** Same order `SquadView`'s and `PublicVerdict`'s own pitches use. */
const POSITION_ORDER = ['GKP', 'DEF', 'MID', 'FWD'] as const;

/**
 * The one shape of `squadId` that encodes a public FPL entry id — see
 * `toEphemeralSquad` in `apps/api/src/routes/publicSquad.ts`, which builds
 * exactly this string for the signed-out `/try` verdict. An authenticated
 * manager's own squad id is an opaque database id with no public entry
 * behind it from this component's point of view, so it never matches.
 */
const PUBLIC_SQUAD_ID = /^public:(\d+):(\d+)$/;

/**
 * The FPL entry id a share *link* can be built for, or `null` when this
 * verdict has no public entry behind it. A link points at
 * `GET /api/share/:id` on the API (T-089), which reconstructs its unfurl
 * page from an entry id and a gameweek alone — it has no other public
 * source of squad data to draw on, so a verdict without one can still be
 * copied as text, just never turned into a link.
 */
export function shareEntryId(analysis: AnalysisResult): number | null {
  const match = PUBLIC_SQUAD_ID.exec(analysis.squadId);
  return match ? Number(match[1]) : null;
}

/** `entryId-gameweek` — the id `apps/api/src/routes/share.ts`'s
 * `parseShareId` expects. */
function shareLinkIdFor(entryId: number, gameweek: number): string {
  return `${entryId}-${gameweek}`;
}

/** `entryId-gameweek` — the id `apps/api/src/routes/share.ts`'s
 * `parseShareId` expects. */
export function shareLinkId(analysis: AnalysisResult): string | null {
  const entryId = shareEntryId(analysis);
  return entryId === null ? null : shareLinkIdFor(entryId, analysis.gameweek);
}

/** Vite inlines this at build time, same as `apps/web/src/api.ts`'s own
 * `BASE` — duplicated rather than imported, since `api.ts` doesn't export
 * it and this ticket doesn't own that file. */
const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * The full share link for a verdict, or `null` when there's no FPL entry id
 * to build one from. `entryId` is an explicit override for a signed-in
 * manager's own squad — `Squad.entryId` (`packages/types/src/squad.ts`) is
 * public FPL data, so `AnalysisView` can pass it straight through even
 * though `analysis.squadId` there is an opaque database id, not the
 * `public:<entryId>:<gameweek>` shape `shareEntryId` parses. Omit it (or
 * pass `null`) to fall back to that parse, which is all a signed-out `/try`
 * verdict has.
 */
export function shareUrl(analysis: AnalysisResult, entryId?: number | null): string | null {
  const resolvedEntryId = entryId ?? shareEntryId(analysis);
  return resolvedEntryId === null ? null : `${API_BASE}/api/share/${shareLinkIdFor(resolvedEntryId, analysis.gameweek)}`;
}

/**
 * The three lines someone pasting into a group chat actually wants — the
 * verdict, the captain call and the transfer, in that order, nothing else.
 * Every value here already appears somewhere in `AnalysisView`; this just
 * puts the three that matter next to each other in a form that reads well
 * outside the app.
 */
export function shareSummary(analysis: AnalysisResult): string {
  const { captain, transfers, gameweek, summary } = analysis;

  const captainLine = captain.alternative
    ? `Captain: ${captain.pick} (or ${captain.alternative})`
    : `Captain: ${captain.pick}`;

  const transferLine =
    transfers.recommended.length === 0
      ? `Transfer: hold — ${transfers.holdReasoning ?? 'nothing worth the move this week'}`
      : transfers.recommended.map((t) => `Transfer: ${t.out} → ${t.in}`).join('\n');

  return [`GW${gameweek} verdict: ${summary}`, captainLine, transferLine].join('\n');
}

interface ShareVerdictProps {
  analysis: AnalysisResult;
}

/**
 * The screenshotted artefact (T-089) — the squad, the one-line call and its
 * reasoning, composed to be legible at phone width and to look like a
 * designed card rather than a cropped page. `aria-hidden`: everything on it
 * already appears, properly labelled, in `AnalysisView` above it — this is
 * a second, visual-only rendering of the same information for capturing,
 * not new content a screen reader needs to announce again.
 */
export function ShareVerdict({ analysis }: ShareVerdictProps) {
  const { gameweek, summary, objectiveReason, captain, transfers, assessments, benchOrder } = analysis;
  const benchNames = new Set(benchOrder);
  const starters = assessments.filter((player) => !benchNames.has(player.name));

  return (
    <div className="share-card" aria-hidden="true">
      <div className="share-card-brand">
        <span className="wordmark">
          The <em>Gaffer</em>
        </span>
        <span className="share-card-gw">GW{gameweek}</span>
      </div>

      <p className="share-card-verdict">{summary}</p>
      <p className="share-card-reason">{objectiveReason}</p>

      <dl className="share-card-callout">
        <dt>Captain</dt>
        <dd>{captain.pick}</dd>
      </dl>

      <dl className="share-card-callout">
        <dt>Transfer</dt>
        <dd>
          {transfers.recommended.length === 0
            ? (transfers.holdReasoning ?? 'Hold — nothing worth the move this week.')
            : transfers.recommended.map((t) => `${t.out} → ${t.in}`).join(', ')}
        </dd>
      </dl>

      {starters.length > 0 && (
        <div className="share-card-squad">
          {POSITION_ORDER.map((position) => {
            const players = starters.filter((player) => player.position === position);
            if (players.length === 0) return null;

            return (
              <div key={position} className="share-card-row">
                {players.map((player) => (
                  <span key={player.name} className="share-card-player">
                    {player.name}
                    {player.name === captain.pick && <span className="pill pill--captain">C</span>}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
